import {locales} from '../../sampleLocations';

const createIntent = (country) => {
  const currency = locales[country].currency;
  const pmTypes =
    country === 'CA'
      ? `listOf(PaymentMethodType.CARD_PRESENT, PaymentMethodType.INTERAC_PRESENT)`
      : `listOf(PaymentMethodType.CARD_PRESENT)`;
  return `
    private val paymentIntentParams = PaymentIntentParameters.Builder(${pmTypes})
            {+mobile-test-cards}
            .setAmount(500)
            {/mobile-test-cards}
            .setCurrency("${currency}")
            .build()
`;
};

const discoveryConfig = (reader) => {
  let method = '';
  if (reader === 'wp3' || reader === 'chipper') {
    method = 'DiscoveryMethod.BLUETOOTH_SCAN';
  } else {
    method = 'DiscoveryMethod.INTERNET';
  }
  return `private val discoveryConfig =
                DiscoveryConfiguration(0, ${method}, true)`;
};

const startPayment = (reader) => {
  let body = '';
  if (reader === 'wp3' || reader === 'chipper') {
    body = `{+create-intent-mobile}
      Terminal.getInstance().createPaymentIntent(paymentIntentParams, createPaymentIntentCallback)
      {/create-intent-mobile}`;
  } else {
    body = `{+fetch-intent}
        // we'll need to create it on the server and then fetch it
        try {
          val secret = ApiClient.createPaymentIntent()
          Terminal.getInstance().retrievePaymentIntent(secret, createPaymentIntentCallback)
        } catch (e: Exception) {
          // handle exception
        }
        {/fetch-intent}`;
  }

  return `private fun startPayment() {
        // Step 1: create payment intent
        ${body}
    }`;
};

export default ({keys, server, reader, country}) => `package com.stripe.example

import android.Manifest
import android.bluetooth.BluetoothAdapter
import android.content.Context
import android.content.Intent
import android.content.pm.PackageManager
import android.location.LocationManager
import android.os.Bundle
import android.provider.Settings
import android.view.ContextThemeWrapper
import android.view.View
import androidx.appcompat.app.AlertDialog
import androidx.appcompat.app.AppCompatActivity
import androidx.core.app.ActivityCompat
import androidx.core.content.ContextCompat
import androidx.recyclerview.widget.LinearLayoutManager
import androidx.recyclerview.widget.RecyclerView
import com.stripe.stripeterminal.Terminal
import com.stripe.stripeterminal.callable.Callback
import com.stripe.stripeterminal.callable.DiscoveryListener
import com.stripe.stripeterminal.callable.PaymentIntentCallback
import com.stripe.stripeterminal.log.LogLevel
import com.stripe.stripeterminal.model.external.*
import java.lang.ref.WeakReference

class MainActivity : AppCompatActivity() {

    companion object {
        // The code that denotes the request for location permissions
        private const val REQUEST_CODE_LOCATION = 1

        ${createIntent(country)}

        ${discoveryConfig(reader)}

        /*** Payment processing callbacks ***/

        // (Step 1 found below in the startPayment function)
        // Step 2 - once we've created the payment intent, it's time to read the card
        private val createPaymentIntentCallback by lazy {
            object : PaymentIntentCallback {
                override fun onSuccess(paymentIntent: PaymentIntent) {
                    {+collect-payment-method}
                    Terminal.getInstance()
                            .collectPaymentMethod(paymentIntent, collectPaymentMethodCallback);
                    {/collect-payment-method}
                }

                override fun onFailure(e: TerminalException) {
                    // Update UI w/ failure
                }
            }
        }

        // Step 3 - we've collected the payment method, so it's time to process the payment
        private val collectPaymentMethodCallback by lazy {
            object : PaymentIntentCallback {
                override fun onSuccess(paymentIntent: PaymentIntent) {
                  {+process-payment}
                  Terminal.getInstance().processPayment(paymentIntent, processPaymentCallback)
                  {/process-payment}
                }

                override fun onFailure(e: TerminalException) {
                    // Update UI w/ failure
                }
            }
        }

        // Step 4 - we've processed the payment! Show a success screen
        private val processPaymentCallback by lazy {
            object : PaymentIntentCallback {
                override fun onSuccess(paymentIntent: PaymentIntent) {
                    ApiClient.capturePaymentIntent(paymentIntent.id);
                }

                override fun onFailure(e: TerminalException) {
                    // Update UI w/ failure
                }
            }
        }
    }

    private val readerClickListener = ReaderClickListener(WeakReference(this))
    private val readerAdapter = ReaderAdapter(readerClickListener)

    /**
     * Upon starting, we should verify we have the permissions we need, then start the app
     */
    override fun onCreate(savedInstanceState: Bundle?) {
        super.onCreate(savedInstanceState)

        setContentView(R.layout.activity_main)

        if (BluetoothAdapter.getDefaultAdapter()?.isEnabled == false) {
            BluetoothAdapter.getDefaultAdapter().enable()
        }

        findViewById<RecyclerView>(R.id.reader_recycler_view).apply {
          adapter = readerAdapter
        }

        findViewById<View>(R.id.discover_button).setOnClickListener {
            discoverReaders()
        }

        findViewById<View>(R.id.collect_payment_button).setOnClickListener {
            startPayment()
        }
    }

    override fun onResume() {
        super.onResume()

        {+check-fine-location}
        // Check for location permissions
        if (ContextCompat.checkSelfPermission(
                        this,
                        Manifest.permission.ACCESS_FINE_LOCATION
                ) == PackageManager.PERMISSION_GRANTED
        ) {
            if (!Terminal.isInitialized() && verifyGpsEnabled()) {
                initialize()
            }
        } else {
            // If we don't have them yet, request them before doing anything else
            val permissions = arrayOf(Manifest.permission.ACCESS_FINE_LOCATION)
            ActivityCompat.requestPermissions(this, permissions, REQUEST_CODE_LOCATION)
        }
        {/check-fine-location}
    }

    /**
     * Receive the result of our permissions check, and initialize if we can
     */
    {+check-location-permission}
    override fun onRequestPermissionsResult(
            requestCode: Int,
            permissions: Array<out String>,
            grantResults: IntArray
    ) {
        super.onRequestPermissionsResult(requestCode, permissions, grantResults)

        // If we receive a response to our permission check, initialize
        if (requestCode == REQUEST_CODE_LOCATION && !Terminal.isInitialized() && verifyGpsEnabled()) {
            initialize()
        }
    }
    {/check-location-permission}

    fun updateReaderConnection(isConnected: Boolean) {
        val recyclerView = findViewById<RecyclerView>(R.id.reader_recycler_view)
        findViewById<View>(R.id.collect_payment_button).visibility =
                if (isConnected) View.VISIBLE else View.INVISIBLE
        findViewById<View>(R.id.discover_button).visibility =
                if (isConnected) View.INVISIBLE else View.VISIBLE
        recyclerView.visibility = if (isConnected) View.INVISIBLE else View.VISIBLE

        if (!isConnected) {
            recyclerView.layoutManager = LinearLayoutManager(this)
            recyclerView.adapter = readerAdapter
        }
    }

    private fun initialize() {
        // Initialize the Terminal as soon as possible
        {+sdk}
        try {
            Terminal.initTerminal(
                    applicationContext, LogLevel.VERBOSE, TokenProvider(),
                    TerminalEventListener()
            )
        } catch (e: TerminalException) {
            throw RuntimeException(
                    "Location services are required in order to initialize " +
                            "the Terminal.",
                    e
            )
        }
        {/sdk}

        val isConnectedToReader = Terminal.getInstance().connectedReader != null
        updateReaderConnection(isConnectedToReader)
    }

    private fun discoverReaders() {
        val discoveryCallback = object : Callback {
            override fun onSuccess() {
                // Update your UI
            }

            override fun onFailure(e: TerminalException) {
                // Update your UI
            }
        }

        val discoveryListener = object : DiscoveryListener {
            override fun onUpdateDiscoveredReaders(readers: List<Reader>) {
                runOnUiThread {
                    readerAdapter.updateReaders(readers)
                }
            }
        }

        {+discover-reader}
        Terminal.getInstance().discoverReaders(discoveryConfig, discoveryListener, discoveryCallback)
        {/discover-reader}
    }

    ${startPayment(reader)}

    private fun verifyGpsEnabled(): Boolean {
        val locationManager: LocationManager? =
                applicationContext.getSystemService(Context.LOCATION_SERVICE) as LocationManager?
        var gpsEnabled = false

        try {
            gpsEnabled = locationManager?.isProviderEnabled(LocationManager.GPS_PROVIDER) ?: false
        } catch (exception: Exception) {}

        if (!gpsEnabled) {
            // notify user
            AlertDialog.Builder(ContextThemeWrapper(this, R.style.Theme_MaterialComponents_DayNight_DarkActionBar))
                    .setMessage("Please enable location services")
                    .setCancelable(false)
                    .setPositiveButton("Open location settings") { param, paramInt ->
                        this.startActivity(Intent(Settings.ACTION_LOCATION_SOURCE_SETTINGS))
                    }
                    .create()
                    .show()
        }

        return gpsEnabled
    }
}
`;
